/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { matchPath } from "react-router";
import { AccountType, CARE_TEAM_MEMBER_ACCOUNT_TYPES_ARRAY } from "../model/userModels";

export type Audience = "parent" | "child" | "careTeam";

export const AUDIENCES: Record<Audience, { accountTypes: readonly AccountType[]; home: string }> = {
  parent: { accountTypes: [AccountType.PARENT], home: "/home" },
  child: { accountTypes: [AccountType.CHILD], home: "/complete-forms" },
  careTeam: { accountTypes: CARE_TEAM_MEMBER_ACCOUNT_TYPES_ARRAY, home: "/patients" }
};

export type RouteEntry = { path: string; audiences: readonly Audience[]; devOnly?: true };

const PUBLIC: readonly Audience[] = [];
const PARENT: readonly Audience[] = ["parent"];
const CARE_TEAM: readonly Audience[] = ["careTeam"];

/**
 * Every path the app serves and who may open it. An empty audience list is public; a gated path
 * shared by two audiences lists both, which is how `/complete-forms` and `/conversations/:id`
 * overlaps are resolved. `routes.tsx` must declare exactly these paths (`routes.test.tsx` checks).
 */
export const ROUTE_TABLE: readonly RouteEntry[] = [
  { path: "/", audiences: PUBLIC },
  { path: "/login", audiences: PUBLIC },
  { path: "/logout", audiences: PUBLIC },
  { path: "/twoFactorAuthPhone", audiences: PUBLIC },
  { path: "/twoFactorAuthSubmitCode", audiences: PUBLIC },
  { path: "/register", audiences: PUBLIC },
  { path: "/forgotPassword", audiences: PUBLIC },
  { path: "/passwordReset", audiences: PUBLIC },
  { path: "/unsubscribe", audiences: PUBLIC },
  { path: "/home", audiences: PARENT },
  { path: "/complete-forms", audiences: ["parent", "child"] },
  { path: "/schedule-appointment", audiences: PARENT },
  { path: "/schedule-intake", audiences: PARENT },
  { path: "/schedule-consult", audiences: PARENT },
  { path: "/add-insurance-card", audiences: PARENT },
  { path: "/conversations", audiences: PARENT },
  { path: "/conversations/:conversationId", audiences: ["parent", "careTeam"] },
  { path: "/provider-selection", audiences: PARENT },
  { path: "/appointments", audiences: PARENT },
  { path: "/patients", audiences: CARE_TEAM },
  { path: "/patient", audiences: CARE_TEAM },
  { path: "/patient/:patientId", audiences: CARE_TEAM },
  { path: "/patient/:patientId/caregivers/:caregiverId/conversations/:conversationId", audiences: CARE_TEAM },
  { path: "/patient/:patientId/conversations/:conversationId", audiences: CARE_TEAM },
  { path: "/patient/:patientId/assessment", audiences: CARE_TEAM },
  { path: "/invite", audiences: CARE_TEAM },
  { path: "/inviteDetails", audiences: CARE_TEAM },
  { path: "/pendingInvites", audiences: CARE_TEAM },
  { path: "/connection", audiences: CARE_TEAM },
  { path: "/addConnection", audiences: CARE_TEAM },
  { path: "/removeConnection", audiences: CARE_TEAM },
  { path: "/myInbox", audiences: CARE_TEAM },
  { path: "/referrals", audiences: CARE_TEAM },
  { path: "/referring-providers", audiences: CARE_TEAM },
  { path: "/referring-organizations", audiences: CARE_TEAM },
  { path: "/organization-members", audiences: CARE_TEAM },
  { path: "/refer", audiences: PUBLIC },
  { path: "/referral", audiences: PUBLIC },
  { path: "/view-referrals", audiences: PUBLIC },
  { path: "/view-providers", audiences: PUBLIC },
  { path: "/auth", audiences: PUBLIC },
  { path: "/consult", audiences: PUBLIC },
  { path: "/pcscreener", audiences: PUBLIC },
  { path: "/screener", audiences: PUBLIC },
  { path: "/lyra", audiences: PUBLIC },
  { path: "/newaccount", audiences: PUBLIC },
  { path: "/newaccountandconsult", audiences: PUBLIC },
  { path: "/forms", audiences: PUBLIC },
  { path: "/dev/booked-intake-preview", audiences: PUBLIC, devOnly: true }
];

export function routeFor(pathname: string): RouteEntry | undefined {
  return ROUTE_TABLE.find(entry => matchPath(entry.path, pathname) !== null);
}

export function audienceOf(accountType: AccountType | string): Audience | undefined {
  const audiences = Object.keys(AUDIENCES) as Audience[];
  return audiences.find(audience => (AUDIENCES[audience].accountTypes as readonly string[]).includes(accountType));
}

/** The landing page for an account; an account with no web audience lands on `/`. */
export function homeFor(accountType: AccountType | string): string {
  const audience = audienceOf(accountType);
  return audience === undefined ? "/" : AUDIENCES[audience].home;
}
